/**
 * Designer "Options" tab: keeps the shopper's selections, applies the rule
 * patches to the loaded design and sends the selections with price quotes
 * and add-to-cart (the server prices them from the stored profile).
 */
import { reactive } from 'vue';
import { buildPatches, expandPatch, resolveSelections, targetedFields } from './mappingEngine';
import { readSelections, writeSelections } from './selectionStore';
import { t } from './i18n';
import type { ConfiguratorProfile, Selections } from './types';

type Item = { id: string; [key: string]: unknown };
type DesignerFacade = {
  updateItem?: (id: string, patch: Record<string, unknown>) => void;
  beginHistoryBatch?: () => void;
  endHistoryBatch?: (reason?: string) => void;
  setPricingContext?: (key: string, value: unknown) => void;
  commands?: { getItems?: () => Record<string, Item[]>; setProductColor?: (index: number) => void };
};

export const controller = reactive({
  profile: null as ConfiguratorProfile | null,
  productId: 0,
  selections: {} as Selections,
  currency: { symbol: '$', decimals: 2, position: 'left' } as { symbol: string; decimals: number; position: string },
});

/** Original values of every layer field a rule can change, captured once. */
const baseline = new Map<string, Record<string, unknown>>();
let lastColor = -1;

function designer(): DesignerFacade | undefined {
  return (window as unknown as { Veyop?: { designer?: DesignerFacade } }).Veyop?.designer;
}

export function money(amount: number): string {
  const c = controller.currency;
  const value = amount.toFixed(Math.max(0, c.decimals ?? 2));
  return (c.position ?? 'left').startsWith('right') ? `${value}${c.symbol}` : `${c.symbol}${value}`;
}

export function resolved() {
  return resolveSelections(controller.profile!, controller.selections, (label) => t('Choose "{label}".').replace('{label}', label));
}

export function init(config: Record<string, unknown>): boolean {
  const sc = config.smartConfigurator as { enabled?: boolean; productId?: number; profile?: ConfiguratorProfile | null } | undefined;
  if (!sc?.enabled || !sc.profile?.options?.length) return false;
  controller.profile = sc.profile;
  controller.productId = Number(sc.productId || 0);
  controller.selections = readSelections(controller.productId);
  const currency = config.currency as { symbol?: string; decimals?: number; position?: string } | undefined;
  if (currency) {
    controller.currency = {
      symbol: String(currency.symbol ?? '$').replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code))),
      decimals: Number(currency.decimals ?? 2),
      position: String(currency.position ?? 'left'),
    };
  }
  return true;
}

export function setSelections(next: Selections): void {
  controller.selections = next;
  writeSelections(controller.productId, next);
  apply();
}

/**
 * Re-apply every rule to the current design (idempotent). Returns false while
 * the designer commands or the design are not ready yet.
 */
export function apply(): boolean {
  const facade = designer();
  if (!controller.profile || !facade) return false;
  const state = resolved();
  facade.setPricingContext?.('smart-configurator', { selections: state.selections });

  const items = facade.commands?.getItems?.();
  if (!items || !facade.updateItem || !Object.values(items).some((list) => list?.length)) return false;
  const byId = new Map<string, Item>();
  for (const list of Object.values(items)) for (const item of list ?? []) byId.set(String(item.id), item);

  const desired = new Map<string, Record<string, unknown>>();
  for (const [layerId, fields] of targetedFields(controller.profile)) {
    const item = byId.get(layerId);
    if (!item) continue;
    if (!baseline.has(layerId)) {
      baseline.set(layerId, Object.fromEntries([...fields].map((field) => [field, item[field]])));
    }
    desired.set(layerId, { ...baseline.get(layerId) });
  }

  for (const patch of buildPatches(controller.profile, state)) {
    if ('layerId' in patch) {
      if (desired.has(patch.layerId)) Object.assign(desired.get(patch.layerId)!, expandPatch(byId.get(patch.layerId)!, patch.patch));
    } else if (patch.colorIndex !== lastColor) {
      lastColor = patch.colorIndex;
      facade.commands?.setProductColor?.(patch.colorIndex);
    }
  }

  const changes: Array<[string, Record<string, unknown>]> = [];
  for (const [layerId, values] of desired) {
    const item = byId.get(layerId)!;
    const diff = Object.fromEntries(Object.entries(values).filter(([field, value]) => value !== undefined && !same(item[field], value)));
    if (Object.keys(diff).length) changes.push([layerId, diff]);
  }
  if (changes.length) {
    facade.beginHistoryBatch?.();
    for (const [layerId, diff] of changes) facade.updateItem(layerId, diff);
    facade.endHistoryBatch?.('smart-configurator');
  }
  return true;
}

function same(a: unknown, b: unknown): boolean {
  return a === b || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
}

/** A newly loaded design (template switch, saved design) needs a fresh baseline. */
export function resetBaseline(): void {
  baseline.clear();
  lastColor = -1;
}
